import React, { useState } from 'react';
import PageHero from '../components/PageHero';
import MartLogo from '../components/MartLogo';
import { franchiseBenefits, retailHighlights, whatsappLink } from '../data/site';
import './pages.css';

const emptyForm = {
    name: '',
    email: '',
    phone: '',
    company: '',
    experience: '',
    city: '',
    investment: '',
    notes: '',
};

const Franchise: React.FC = () => {
    const [form, setForm] = useState(emptyForm);
    const [sent, setSent] = useState(false);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const url = whatsappLink('Kifayti — Mall Franchise Enquiry', {
            Name: form.name,
            Email: form.email,
            Phone: form.phone,
            Company: form.company,
            'Business experience': form.experience,
            'Preferred city / area': form.city,
            'Investment capacity': form.investment,
            Notes: form.notes,
        });

        window.open(url, '_blank', 'noopener,noreferrer');
        setForm(emptyForm);
        setSent(true);
        window.setTimeout(() => setSent(false), 6000);
    };

    return (
        <>
            <PageHero
                kicker="Retail franchise"
                title={
                    <>
                        Open a Kifayti mall in <span className="grad-text">your city</span>
                    </>
                }
                description="Kifayti SuperMart is expanding through franchise partners. Partners get a tested retail model, our in-house retail technology, an exclusive territory and hands-on setup support."
            >
                <div className="chip-row">
                    {retailHighlights.map((item, i) => (
                        <span className="chip chip--dark" key={item} style={{ ['--i' as string]: i }}>
                            {item}
                        </span>
                    ))}
                </div>
            </PageHero>

            <section className="section section--light">
                <div className="container">
                    <div className="sec-head" data-reveal="up">
                        <span className="kicker">What partners get</span>
                        <h2>A retail business with the technology already solved</h2>
                        <p className="lede">
                            Most retail franchises hand you a brand and leave the systems to you.
                            Because we build software as our main business, franchise partners run
                            on our own billing, inventory, payroll and signage platforms from day
                            one.
                        </p>
                    </div>

                    <div className="grid grid--3">
                        {franchiseBenefits.map((benefit, i) => (
                            <article
                                className="card service-card"
                                key={benefit.title}
                                data-reveal="up"
                                style={{ ['--i' as string]: i % 3 }}
                            >
                                <span className="service-card__icon" aria-hidden="true">
                                    {benefit.icon}
                                </span>
                                <h3>{benefit.title}</h3>
                                <p>{benefit.description}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="section section--alt" id="apply">
                <div className="container form-layout">
                    <div className="form-layout__intro">
                        <div className="mart-plate mart-plate--compact" data-reveal="zoom">
                            <MartLogo size="lg" />
                        </div>

                        <span className="kicker">Apply</span>
                        <h2>Franchise enquiry</h2>
                        <p className="lede">
                            Share a few details and our franchise team will get back to you with
                            the model, investment range and territory availability for your area.
                        </p>
                        <ul className="tick-list tick-list--stacked">
                            {[
                                'Site evaluation and catchment analysis',
                                'Fit-out planning and vendor onboarding',
                                'Staff training and operating procedures',
                                'Launch marketing and ongoing brand support',
                            ].map((item, i) => (
                                <li key={item} data-reveal="left" style={{ ['--i' as string]: i }}>
                                    <span className="tick" aria-hidden="true">
                                        ✓
                                    </span>
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="form-card" data-reveal="right">
                        {sent && (
                            <p className="form-success" role="status">
                                Thanks — WhatsApp has opened with your enquiry. Send the message and
                                our franchise team will reply shortly.
                            </p>
                        )}

                        <form onSubmit={handleSubmit} className="form-stack">
                            <div className="field-row">
                                <div className="field">
                                    <label htmlFor="f-name">Full name *</label>
                                    <input
                                        id="f-name"
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div className="field">
                                    <label htmlFor="f-email">Email *</label>
                                    <input
                                        id="f-email"
                                        name="email"
                                        type="email"
                                        value={form.email}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="field-row">
                                <div className="field">
                                    <label htmlFor="f-phone">Phone *</label>
                                    <input
                                        id="f-phone"
                                        name="phone"
                                        type="tel"
                                        value={form.phone}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div className="field">
                                    <label htmlFor="f-company">Company (if any)</label>
                                    <input
                                        id="f-company"
                                        name="company"
                                        value={form.company}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>

                            <div className="field-row">
                                <div className="field">
                                    <label htmlFor="f-exp">Business experience *</label>
                                    <select
                                        id="f-exp"
                                        name="experience"
                                        value={form.experience}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">Select experience level</option>
                                        <option>No business experience</option>
                                        <option>Retail experience</option>
                                        <option>Franchise experience</option>
                                        <option>Management experience</option>
                                        <option>Entrepreneur</option>
                                    </select>
                                </div>
                                <div className="field">
                                    <label htmlFor="f-city">Preferred city / area *</label>
                                    <input
                                        id="f-city"
                                        name="city"
                                        value={form.city}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="field">
                                <label htmlFor="f-invest">Investment capacity</label>
                                <select
                                    id="f-invest"
                                    name="investment"
                                    value={form.investment}
                                    onChange={handleChange}
                                >
                                    <option value="">Prefer to discuss</option>
                                    <option>Under ₹25 lakh</option>
                                    <option>₹25–50 lakh</option>
                                    <option>₹50 lakh – ₹1 crore</option>
                                    <option>Above ₹1 crore</option>
                                </select>
                            </div>

                            <div className="field">
                                <label htmlFor="f-notes">Anything else</label>
                                <textarea
                                    id="f-notes"
                                    name="notes"
                                    value={form.notes}
                                    onChange={handleChange}
                                    placeholder="Site availability, timeline, questions…"
                                />
                            </div>

                            <button type="submit" className="btn btn--primary btn--lg">
                                Send enquiry via WhatsApp
                            </button>
                            <p className="form-note">
                                Submitting opens WhatsApp with your details pre-filled. Nothing is
                                sent until you press send there.
                            </p>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Franchise;
